<script lang="ts">
  import { onMount } from "svelte"
  import { ChevronDown, ChevronRight, BadgeCheck } from "lucide-svelte"
  import { fade, slide } from "svelte/transition"
  import { xhr, toast } from "$app/utils"
  import { appStore } from "$app/store.svelte"

  let {
    bookReading
  }: {
    bookReading: any
  } = $props()

  let current_reading_checkbox = $state(null)

  let current_book = $state(bookReading?.current_book)
  let last_book = $state(bookReading?.last_book)

  let next_reading = $state(false)
  let upcoming_readings = $state([])

  onMount(() => {
    if (current_book?.upcoming_readings?.length >= 1) {
      next_reading = [...current_book.upcoming_readings].shift()
    }
    if (current_book?.upcoming_readings?.length > 1) {
      upcoming_readings = current_book.upcoming_readings.slice(1, 4)
    }
  })

  function updateBookReading(book_reading, status) {
    let status_string = "not_started"
    if (status) {
      status_string = "completed"
    }
    return xhr
      .patch(
        `/plans/${appStore.plan.id}/plan_book_readings/${book_reading.id}.json`,
        {
          plan_book_reading: { status: status_string }
        }
      )
      .then(function (resp) {
        bookReading = resp.data
        current_book = bookReading?.current_book
        last_book = bookReading?.last_book
        if (current_book?.upcoming_readings?.length >= 1) {
          next_reading = [...current_book.upcoming_readings].shift()
        }
        if (current_book?.upcoming_readings?.length > 1) {
          upcoming_readings = current_book.upcoming_readings.slice(1, 4)
        } else {
          upcoming_readings = []
        }
        current_reading_checkbox.checked = false
      })
      .catch(function (error) {
        toast("Error updating status", "error")
      })
  }

  let visibility = $state(true)
</script>

<div class="w-full mt-4" id="book_reading">
  <h2 class="mt-6 mb-4">
    <button
      onclick={() => (visibility = !visibility)}
      class="flex justify-start items-center"
    >
      {#if visibility}
        <ChevronDown class="size-6 text-gray-500" />
      {:else}
        <ChevronRight class="size-6 text-gray-500" />
      {/if}
      <span class="text-2xl lg:text-3xl text-black ml-2"
        >Leadership Development
      </span>
    </button>
  </h2>

  {#if visibility}
    <div
      class="ml-2 lg:ml-10"
      transition:slide={{
        delay: 100,
        duration: 300
      }}
    >
      {#if current_book}
        <div
          class="text-center text-gray-500 text-xl bg-gray-50 rounded-lg py-4 px-4"
        >
          <div class="grid grid-cols-1 gap-2 lg:grid-cols-4">
            <div class="flex flex-col items-center justify-center relative">
              <p
                class="text-sm mb-1 font-bold absolute bottom-4 text-center p-1 bg-green-600 text-white rounded shadow-lg"
              >
                Currently Reading
              </p>
              <img
                src={current_book.image_url}
                class="w-auto h-56"
                alt={current_book.title}
              />
            </div>
            <div class="lg:col-span-3">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div
                  class="bg-green-100 border border-gray-200 rounded-lg py-2"
                >
                  <p class="text-lg px-4 text-green-800">Next Readings</p>
                  <div class="mt-2 border-t border-green-800 py-4 px-4">
                    <div
                      class="relative flex items-center justify-between px-1"
                    >
                      {#if next_reading}
                        <div class="text-lg font-bold text-gray-800">
                          <span class="text-gray-500 text-sm mr-1"
                            >{next_reading?.position}</span
                          >
                          {next_reading?.title}
                        </div>
                      {/if}
                      {#if appStore.is_leadership && next_reading}
                        <div class="ml-3 flex items-center">
                          <label
                            for="book_reading_{next_reading.id}"
                            class="mt-0 pt-0 text-sm text-gray-700"
                            >Completed</label
                          >
                          <input
                            id="book_reading_{next_reading.id}"
                            type="checkbox"
                            bind:this={current_reading_checkbox}
                            onchange={(e) => {
                              updateBookReading(next_reading, e.target.checked)
                            }}
                            class="ml-2 h-5 w-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                      {/if}
                    </div>
                    {#if upcoming_readings && upcoming_readings.length > 0}
                      <ul
                        class="mt-4 border-t border-b border-gray-300 max-h-24 overflow-y-scroll"
                      >
                        {#each upcoming_readings as book_reading (book_reading.id)}
                          <li
                            in:fade
                            out:fade
                            class="flex justify-start items-center py-2 border-b border-gray-200 mx-2"
                          >
                            <div class="text-gray-500 text-xs mr-2">
                              {book_reading.position}
                            </div>
                            <div class="text-sm text-black">
                              {book_reading.title}
                            </div>
                          </li>
                        {/each}
                      </ul>
                    {:else}
                      <p class="text-sm font-bold test-center mt-6">
                        {#if upcoming_readings.length === 0}
                          No readings assigned to this book.
                        {:else}
                          No more upcoming readings
                        {/if}
                      </p>
                    {/if}
                  </div>
                </div>
                <div class="bg-gray-100 border border-gray-200 rounded-lg py-2">
                  <p class="text-lg px-4 text-gray-800">Completed Readings</p>
                  <div
                    class="mt-2 border-t border-green-800 py-2 px-4 relative"
                  >
                    {#if current_book.completed_readings && current_book.completed_readings.length > 0}
                      {#if appStore.is_leadership}
                        <div class="absolute top-2 right-12">
                          <button
                            onclick={(e) => {
                              updateBookReading(
                                current_book.completed_readings[0],
                                false
                              )
                            }}
                            class="bg-gray-100 text-xs rounded border border-gray-300 p-1 hover:bg-gray-200"
                            >Undo</button
                          >
                        </div>
                      {/if}
                      <ul class="h-36 overflow-y-scroll">
                        {#each current_book.completed_readings as book_reading (book_reading.id)}
                          <li
                            in:fade
                            out:fade
                            class="flex justify-start items-center py-2 border-b border-gray-200 mx-2"
                          >
                            <div class="text-gray-500 text-xs mr-2">
                              {book_reading.position}
                            </div>
                            <div class="text-base text-black px-2">
                              {book_reading.title}
                            </div>
                          </li>
                        {/each}
                      </ul>
                    {:else}
                      <div class="mt-2 text-base">
                        You have not completed any readings yet.
                      </div>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      {:else if last_book}
        <div
          class="text-center text-gray-500 text-xl bg-gray-50 rounded-lg py-4 px-4"
        >
          <div class="grid grid-cols-1 md:grid-cols-3 py-2 px-4">
            <div class="flex items-center justify-center flex-col">
              <BadgeCheck class="size-12 text-green-500" />
              <p class="text-xl flex items-center justify-start text-green-600">
                Congratulations! You have completed:
              </p>
              <span class="mt-2 text-lg font-bold">{last_book.title}</span>
            </div>
            <div class="flex items-center justify-center">
              <img
                src={last_book.image_url}
                class="w-auto h-48"
                alt={last_book.title}
              />
            </div>
            <div
              class="flex justify-center items-center flex-col text-gray-500 text-xl bg-gray-50 rounded-lg py-4 px-4"
            >
              <p>It's time to pick your next book!</p>
              <a
                href="/plans/{appStore.plan.id}/books"
                class="btn btn-primary mt-3">Find a book to read</a
              >
            </div>
          </div>
        </div>
      {:else}
        <div
          class="text-center text-gray-500 text-xl bg-gray-50 rounded-lg py-4 px-4"
        >
          <p>You are not currently reading a book.</p>
          <a href="/plans/{appStore.plan.id}/books" class="btn btn-primary mt-3"
            >Find a book to read</a
          >
        </div>
      {/if}
    </div>
  {/if}
</div>
